import { useIsMobile } from "@/hooks/useIsMobile";
import { useEffect, useState } from "react";
import classes from "@/styles/fulfilment-partners/shipvistaConnect2.module.css";
import Image from "next/image";
import * as yup from "yup";
import { ImCross } from "react-icons/im";
import { TiTickOutline } from "react-icons/ti";
import { useForm, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Button,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  List,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { shipvistaConnectPage } from "@/utils/content";
import { shipVistaConnectEmail } from "@/services/connectWithUsServices";
import useAlertBar from "@/hooks/useAlertBar";
import InputGroup from "../common/input-group";

type Inputs = {
  firstName: string;
  lastName: string;
  companyName: string;
  phone: string;
  email: string;
  estimatedAnnualVolume: string;
  customEstimatedAnnualVolume: string | undefined;
  distributionCenterAddress: string;
  shipmentIndustry: string;
  deliverySetting: string[] | undefined;
  otherIndustry: string | undefined;
  website: string | undefined;
};

const validationSchema = yup.object().shape({
  firstName: yup.string().required("First Name is required"),
  lastName: yup.string().required("Last Name is required"),
  companyName: yup.string().required("Company Name is required"),
  phone: yup
    .string()
    .matches(/^[0-9]+$/, "Phone number is not valid")
    .min(10, "Phone number is too short")
    .max(10, "Phone number is too long")
    .required("Phone number is required"),
  email: yup.string().email("Invalid email").required("Email is required"),
  website: yup.string().url("Invalid URL, HINT: https://www.shipvista.com/"),
  estimatedAnnualVolume: yup
    .string()
    .required("Estimated annual volume is required"),
  customEstimatedAnnualVolume: yup.string().when("estimatedAnnualVolume", {
    is: (val: string) => val === "custom",
    then: (schema) => schema.required("Please specify your estimated volume"),
    otherwise: (schema) => schema.notRequired(),
  }),
  distributionCenterAddress: yup.string().required("Address is required"),
  shipmentIndustry: yup.string().required("Shipment Industry is required"),
  deliverySetting: yup.array().min(1),
  otherIndustry: yup.string().when("shipmentIndustry", {
    is: (val: string) => val === "other",
    then: (schema) => schema.required("Please specify your industry"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

export default function ShipvistaConnectMain2() {
  const { openAlertBar } = useAlertBar();
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState("0");
  const [showOtherIndustry, setShowOtherIndustry] = useState(false);
  const [showCustomEstimatedAnnualVolume, setShowCustomEstimatedAnnualVolume] =
    useState(false);

  //@ts-ignore
  const toggle = (id) => {
    if (open === id) {
      //@ts-ignore
      setOpen();
    } else {
      setOpen(id);
    }
  };

  const content = shipvistaConnectPage.en;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<Inputs>({
    resolver: yupResolver(validationSchema),
    criteriaMode: "all",

    shouldFocusError: true,
  });

  const shipmentIndustryValue = watch("shipmentIndustry");
  const estimatedAnnualVolumeValue = watch("estimatedAnnualVolume");

  useEffect(() => {
    setShowOtherIndustry(shipmentIndustryValue === "other");
    setShowCustomEstimatedAnnualVolume(estimatedAnnualVolumeValue === "custom");
  }, [shipmentIndustryValue, estimatedAnnualVolumeValue]);

  const onSubmit: SubmitHandler<Inputs> = async (formDataJSON) => {
    // setLoading(true);

    const requestBody = {
      ...formDataJSON,
    };
    // console.log(requestBody);

    const formData = new FormData();
    for (const key in requestBody) {
      formData.append(key, requestBody[key as keyof Inputs] as string | Blob);
    }

    try {
      let { data } = await shipVistaConnectEmail(formData);
      setLoading(false);
      openAlertBar(data.message, data.status);
      reset();
    } catch (error) {
      setLoading(false);
      openAlertBar(
        "Unexpected error occurred while submitting the form. Please try again.",
        false
      );
      console.error(error);
    }
  };

  return (
    <div className={isMobile ? classes.containerMobile : classes.container}>
      {/* Margin */}
      <div style={{ marginBottom: "50px" }}></div>
      <section className="my-3 container">
        {/* Section 1 */}
        <Row sm={1} xs={1} md={2} className={classes.rowImage_margin}>
          <Col
            className={
              isMobile ? classes.pageImageTextMobile : classes.pageImageText
            }
          >
            <h1 className={classes.text_normal}>{content.section1.heading}</h1>
            <br />
            <p>{content.section1.para1}</p>
            <br />
            <p>{content.section1.para2}</p>
            <br />
            <Button color="dark">
              <a style={{ color: "inherit" }} href="#Shipvista-Connect-Service">
                {content.section1.buttonTitle}
              </a>
            </Button>
          </Col>
          {isMobile && <div style={{ marginBottom: "10px" }}></div>}
          <Col>
            <Image
              src={content.section1.img}
              className={classes.pageImage}
              alt="Stats Image"
              width={500}
              height={500}
              quality={100}
            />
          </Col>
        </Row>
        {/* Section 2 */}
        <Container>
          <h2
            className={isMobile ? classes.iconHeaderMobile : classes.iconHeader}
          >
            {content.section2.heading}
          </h2>
          <Row xs={1} sm={2} md={3}>
            {content.section2.iconList.map((icon, i) => (
              <Col key={i}>
                <div
                  className={
                    isMobile
                      ? classes.iconContainerMobile
                      : classes.iconContainer
                  }
                >
                  <div className={isMobile ? classes.iconMobile : classes.icon}>
                    <Image
                      src={icon.img}
                      alt={icon.img}
                      width={53}
                      height={53}
                      quality={100}
                    />
                  </div>
                  <h5>{icon.heading}</h5>
                  <p>{icon.sub}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>

        {/* Margin */}
        <div style={{ marginBottom: "100px" }}></div>
        {/* Section 3 */}
        <Row sm={1} xs={1} md={2} className={classes.rowImage_margin}>
          <Col>
            <Image
              src={content.sectin3.img}
              className={classes.pageImage}
              alt="Stats Image"
              width={500}
              height={500}
              quality={100}
            />
          </Col>
          {isMobile && <div style={{ marginBottom: "10px" }}></div>}
          <Col
            className={
              isMobile ? classes.pageImageTextMobile : classes.pageImageText
            }
          >
            <h2 className={classes.text_normal}>{content.sectin3.heading}</h2>
            <br />
            <p>{content.sectin3.para}</p>

            <List type="unstyled">
              {content.sectin3.points.map((point, i) => (
                <li key={i}>
                  <b>{point.head}</b> {point.sub}
                </li>
              ))}
            </List>
          </Col>
        </Row>
        {/* Section 4 */}
        <Row id="Shipvista-Connect-Service" className={classes.rowImage_margin}>
          <h2 className={classes.text_normal}>{content.section4.heading}</h2>
          <p>{content.section4.para}</p>

          {/* Margin */}
          <div style={{ marginBottom: "20px" }}></div>

          <h3>{content.section4.tableHeading}</h3>
          <Container>
            <Table striped>
              <thead>
                <tr className="table-dark">
                  {content.section4.table.col.map((c, i) => (
                    <th key={i}>{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {content.section4.table.content.map((r, i) => (
                  <tr key={i}>
                    {r.map((c, i) => (
                      <td key={i}>
                        {i === 1 ? (
                          c === "Yes" ? (
                            <TiTickOutline color="green" />
                          ) : (
                            <ImCross color="red" />
                          )
                        ) : (
                          ""
                        )}{" "}
                        {c}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </Container>

          {/* Margin */}
          <div style={{ marginBottom: "20px" }}></div>

          <h3>{content.section4.tableHeading2}</h3>
          <Container style={{ textAlign: "center" }}>
            <Table responsive>
              <thead>
                <tr className="table-dark">
                  {content.section4.tableService.col.map((c, i) => (
                    <th key={i}>{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {content.section4.tableService.content.map((r, i) => (
                  <tr key={i}>
                    {r.map((c, i) => (
                      <td key={i}>{c}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </Container>
        </Row>

        {/* Section 5 */}
        <Row className={classes.rowImage_margin}>
          <h2 className={classes.text_normal}>{content.section5.heading}</h2>
          <p>{content.section5.para}</p>
          <h3>{content.section5.heading2A}</h3>
          <List>
            <ul>
              <li style={{ color: `${content.section5.point[1]}` }}>
                {content.section5.point[0]}
              </li>
            </ul>
          </List>

          <h3>{content.section5.heading2B}</h3>
          <List>
            <ul>
              <li style={{ color: `${content.section5.point1[1]}` }}>
                {content.section5.point1[0]}
              </li>
              <li style={{ color: `${content.section5.point2[1]}` }}>
                {content.section5.point2[0]}
              </li>
            </ul>
          </List>
        </Row>
        {/* Section 6 */}
        {/* <Row
          sm={1}
          xs={1}
          md={1}
          style={{ maxWidth: isMobile ? "100%" : "60%" }}
          className="mx-auto"
        > */}
        {/* <Col> */}
        <h2 className={classes.text_normal}>{content.section6.heading}</h2>
        <h3>{content.section6.sub}</h3>
        {/* </Col> */}
        {/* <Col> */}
        <Form
          onSubmit={handleSubmit(onSubmit)}
          className={`my-4 needs-validation ${isMobile ? "p-2" : "p-4"}`}
        >
          <Row>
            <Col xs={12} lg={4}>
              <InputGroup
                required
                label="First Name"
                name="firstName"
                register={register}
                error={errors.firstName?.message}
              />
            </Col>
            <Col xs={12} lg={4}>
              <InputGroup
                required
                label="Last Name"
                name="lastName"
                register={register}
                error={errors.lastName?.message}
              />
            </Col>
            <Col xs={12} lg={4}>
              <InputGroup
                required
                label="Company Name"
                name="companyName"
                register={register}
                error={errors.companyName?.message}
              />
            </Col>
            <Col xs={12} lg={4}>
              <InputGroup
                required
                label="Phone"
                name="phone"
                type="tel"
                register={register}
                error={errors.phone?.message}
              />
            </Col>
            <Col xs={12} lg={4}>
              <InputGroup
                required
                label="Email"
                name="email"
                type="email"
                register={register}
                error={errors.email?.message}
              />
            </Col>
            <Col xs={12} lg={4}>
              <InputGroup
                label="Website"
                name="website"
                type="website"
                register={register}
                error={errors.website?.message}
              />
            </Col>
            <InputGroup
              required
              label="Estimated Annual Volume"
              name="estimatedAnnualVolume"
              type="select"
              options={content.ESTIMATED_ANNUAL_VOLUME_OPTIONS}
              register={register}
              error={errors.estimatedAnnualVolume?.message}
            />
            {showCustomEstimatedAnnualVolume && (
              <InputGroup
                required
                label="Custom Estimated Annual Volume"
                name="customEstimatedAnnualVolume"
                type="string"
                register={register}
                error={errors.estimatedAnnualVolume?.message}
              />
            )}
            <InputGroup
              required
              label="Distribution Center Address"
              name="distributionCenterAddress"
              register={register}
              error={errors.distributionCenterAddress?.message}
            />
            <InputGroup
              required
              label="Shipment Industry"
              name="shipmentIndustry"
              type="select"
              options={content.SHIPMENT_INDUSTRY_OPTIONS}
              register={register}
              error={errors.shipmentIndustry?.message}
            />
            {showOtherIndustry && (
              <InputGroup
                required
                label="Other Industry"
                name="otherIndustry"
                register={register}
                error={errors.otherIndustry?.message}
              />
            )}
          </Row>
          <FormGroup>
            <Label>
              What type of delivery setting do you operate in?{" "}
              <span className="text-danger"> *</span>
            </Label>{" "}
            <br />
            {content.section6.checkboxOpt.map(({ name, order }, index) => {
              return (
                <div key={index}>
                  <div>
                    <input
                      type="checkbox"
                      {...register("deliverySetting")}
                      value={name}
                    />{" "}
                    {name}
                  </div>
                </div>
              );
            })}
            {errors.deliverySetting && (
              <div className={classes.error}>
                Please select at least one option
              </div>
            )}
          </FormGroup>

          <Button
            type="submit"
            disabled={loading}
            className={`w-100 mt-3 bg-danger ${isMobile ? "py-2" : "py-3"}`}
          >
            Submit
            {loading && <Spinner size="sm" className="ms-3" />}
          </Button>
        </Form>
        {/* </Col> */}
        {/* </Row> */}
        {/* Margin */}
        <div style={{ marginBottom: "50px" }}></div>
        {/* Section 7 FAQ */}
        <h2 className={classes.text_normal}>{content.section7.heading}</h2>
        <br />
        <div>
          {/*@ts-ignore*/}
          <Accordion open={open} toggle={toggle}>
            {content.section7.faqs.map((faq, i) => (
              <AccordionItem key={i}>
                <AccordionHeader targetId={`${i}`}>
                  {faq.question}
                </AccordionHeader>
                <AccordionBody accordionId={`${i}`}>{faq.answer}</AccordionBody>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
}
